import React, { useState } from 'react';
import { BarChart3, TrendingUp, Package, Calendar, DollarSign, FileText } from 'lucide-react';
import { reportService } from '../../api/services';
import { formatCurrency, formatNumber, exportToExcel } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import { Button, PageHeader } from '../../components/UI';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import DataTable from '../../components/DataTable';
import toast from 'react-hot-toast';

const REPORTS = [
  { key: 'sales', label: 'Sales Report', icon: TrendingUp, color: 'green' },
  { key: 'purchases', label: 'Purchases Report', icon: BarChart3, color: 'blue' },
  { key: 'inventory', label: 'Inventory Report', icon: Package, color: 'purple' },
  { key: 'expiry', label: 'Expiry Report', icon: Calendar, color: 'orange' },
  { key: 'profit', label: 'Profit & Loss', icon: DollarSign, color: 'green' },
];

export default function ReportsPage() {
  const [activeReport, setActiveReport] = useState(null);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ from: '', to: '' });

  const colorMap = { green: 'bg-green-50 text-green-700 border-green-200', blue: 'bg-blue-50 text-blue-700 border-blue-200', purple: 'bg-purple-50 text-purple-700 border-purple-200', orange: 'bg-orange-50 text-orange-700 border-orange-200' };

  const loadReport = async (type) => {
    setActiveReport(type);
    setLoading(true);
    try {
      let res;
      const params = { from: filters.from || undefined, to: filters.to || undefined };
      switch (type) {
        case 'sales': res = await reportService.getSales(params); break;
        case 'purchases': res = await reportService.getPurchases(params); break;
        case 'inventory': res = await reportService.getInventory(params); break;
        case 'expiry': res = await reportService.getExpiry(params); break;
        case 'profit': res = await reportService.getProfit(params); break;
        default: return;
      }
      setData(res.data.data || (type === 'profit' ? [] : []));
      setSummary(res.data.summary || res.data.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const getColumns = () => {
    switch (activeReport) {
      case 'sales': return [
        { key: 'invoiceNumber', label: 'Invoice No.' },
        { key: 'customer', label: 'Customer', render: (v) => v?.name || 'Walk-in' },
        { key: 'totalAmount', label: 'Amount', render: (v) => formatCurrency(v) },
        { key: 'paymentMethod', label: 'Payment', render: (v) => <span className="capitalize">{v}</span> },
        { key: 'createdAt', label: 'Date', render: (v) => toJalali(v) },
      ];
      case 'purchases': return [
        { key: 'invoiceNumber', label: 'Invoice No.' },
        { key: 'supplier', label: 'Supplier', render: (v) => v?.name || '-' },
        { key: 'totalAmount', label: 'Total', render: (v) => formatCurrency(v) },
        { key: 'status', label: 'Status' },
        { key: 'createdAt', label: 'Date', render: (v) => toJalali(v) },
      ];
      case 'inventory': return [
        { key: 'medicine', label: 'Medicine', render: (v) => v?.commercialName || '-' },
        { key: 'quantity', label: 'Qty' },
        { key: 'purchasePrice', label: 'Buy Price', render: (v) => formatCurrency(v) },
        { key: 'sellingPrice', label: 'Sell Price', render: (v) => formatCurrency(v) },
        { key: 'expiryDate', label: 'Expiry', render: (v) => toJalali(v) },
      ];
      case 'expiry': return [
        { key: 'medicine', label: 'Medicine', render: (v) => v?.commercialName || '-' },
        { key: 'batchNumber', label: 'Batch No.' },
        { key: 'quantity', label: 'Qty' },
        { key: 'expiryDate', label: 'Expiry Date', render: (v) => <span className="text-red-600 font-medium">{toJalali(v)}</span> },
      ];
      default: return [];
    }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Generate and export business reports" />

      {/* Report Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        {REPORTS.map(({ key, label, icon: Icon, color }) => (
          <button key={key} onClick={() => loadReport(key)}
            className={`p-4 rounded-xl border text-left transition-all hover:shadow-md ${activeReport === key ? colorMap[color] + ' border-2' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
            <Icon size={20} className={activeReport === key ? '' : 'text-gray-500'} />
            <p className={`text-sm font-medium mt-2 ${activeReport === key ? '' : 'text-gray-700'}`}>{label}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      {activeReport && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
            <JalaliDatePicker value={filters.from} onChange={v=>setFilters({...filters,from:v})} placeholder="From date" className="w-40"/>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
            <JalaliDatePicker value={filters.to} onChange={v=>setFilters({...filters,to:v})} placeholder="To date" className="w-40"/>
          </div>
          <Button onClick={() => loadReport(activeReport)} loading={loading}>Generate</Button>
          {data.length > 0 && (
            <Button variant="outline" onClick={() => exportToExcel(data, activeReport)}>Export Excel</Button>
          )}
        </div>
      )}

      {/* Profit Summary */}
      {activeReport === 'profit' && summary && !Array.isArray(summary) && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          {[
            { label: 'Revenue', value: formatCurrency(summary.revenue), color: 'text-green-700' },
            { label: 'Cost of Goods', value: formatCurrency(summary.cost), color: 'text-gray-700' },
            { label: 'Gross Profit', value: formatCurrency(summary.grossProfit), color: 'text-blue-700' },
            { label: 'Expenses', value: formatCurrency(summary.expenses), color: 'text-orange-700' },
            { label: 'Net Profit', value: formatCurrency(summary.netProfit), color: summary.netProfit >= 0 ? 'text-green-700' : 'text-red-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
              <p className={`text-xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Summary Cards for other reports */}
      {summary && Array.isArray(data) && activeReport !== 'profit' && summary.total !== undefined && (
        <div className="flex gap-4 mb-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Records</p>
            <p className="text-xl font-bold text-gray-800">{formatNumber(summary.count)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total Value</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(summary.total || summary.totalValue)}</p>
          </div>
        </div>
      )}

      {/* Table */}
      {activeReport && activeReport !== 'profit' && (
        <DataTable
          columns={getColumns()}
          data={data}
          loading={loading}
          totalItems={data.length}
          title={activeReport}
          exportFilename={activeReport + '_report'}
        />
      )}

      {!activeReport && (
        <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center h-64">
          <div className="text-center">
            <FileText size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">Select a report type above to get started</p>
          </div>
        </div>
      )}
    </div>
  );
}
